import React, { Component } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { collection, doc, getDoc, getFirestore } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from 'firebase/auth';
import '../../index.css';
import ModernLanding from './ModernLanding';
import PurchaseList from '../Userprofile/Purchaselist';
import Menu from '../Sidepanel/Leftmenu';
import Footer from '../Style/Footer';
import ShopItem from '../Shop/Shopitem';
import Completion from '../Shop/Completion';
import Kart from '../Shop/ShopCart';
import SupportForm from '../Support/Supportform';

const db = getFirestore();
const DEFAULT_YANDEX_AUTH_URL =
  'https://europe-west1-ziel-studienkolleg.cloudfunctions.net/YandexAuth';

export default class ConnectUI extends Component {
  state = {
    isSignedIn: false,
    totalNumber: null,
    examNumber: null,
    themesNumber: null,
    isYandexLoading: false,
    yandexPasswordMode: '',
    yandexPasswordTicket: '',
    yandexEmail: '',
    yandexPassword: '',
    yandexPasswordConfirmation: '',
    isYandexPasswordLoading: false,
    email: '',
    password: '',
    isRegistering: false,
    isEmailAuthLoading: false,
    authError: '',
    authNotice: '',
  };

  async componentDidMount() {
    this.unregisterAuthObserver = getAuth().onAuthStateChanged((user) => {
      this.setState({ isSignedIn: Boolean(user) });
    });

    await this.completeYandexSignIn();

    this.fetchData()
      .then((result) => {
        if (!result) return;
        this.setState({
          totalNumber: result.Total,
          examNumber: result.Exam,
          themesNumber: result.Themes,
        });
      })
      .catch((error) => {
        console.error('Error fetching landing page statistics:', error);
      });
  }

  componentWillUnmount() {
    if (this.unregisterAuthObserver) {
      this.unregisterAuthObserver();
    }
  }

  fetchData = async () => {
    const docRef = doc(collection(db, 'Aufgaben'), 'Insgesamt');
    const snapshot = await getDoc(docRef);
    return snapshot.data();
  };

  completeYandexSignIn = async () => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const customToken = params.get('yandex_token');
    const yandexError = params.get('yandex_error');
    const yandexPasswordTicket = params.get('yandex_password_ticket');
    const yandexEmail = params.get('yandex_email');
    const yandexPasswordMode = params.get('yandex_password_mode');

    if (!customToken && !yandexError && !yandexPasswordTicket) return false;

    window.history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${window.location.search}`
    );

    if (yandexError) {
      this.setState({
        isYandexLoading: false,
        authError: yandexError === 'email_required'
          ? 'Яндекс не передал адрес электронной почты. Разрешите доступ к email и попробуйте ещё раз.'
          : 'Войти через Яндекс не получилось. Попробуйте ещё раз.',
        authNotice: '',
      });
      return false;
    }

    if (
      yandexPasswordTicket &&
      yandexEmail &&
      ['setup', 'signin'].includes(yandexPasswordMode)
    ) {
      this.setState({
        isYandexLoading: false,
        yandexPasswordMode,
        yandexPasswordTicket,
        yandexEmail,
        yandexPassword: '',
        yandexPasswordConfirmation: '',
        authError: '',
        authNotice: '',
      });
      return true;
    }

    this.setState({ isYandexLoading: true, authError: '', authNotice: '' });
    try {
      await signInWithCustomToken(getAuth(), customToken);
      return true;
    } catch (error) {
      console.error('Yandex Firebase sign-in failed:', error);
      this.setState({
        isYandexLoading: false,
        authError: 'Не удалось завершить вход через Яндекс. Попробуйте ещё раз.',
      });
      return false;
    }
  };

  signInWithYandex = () => {
    const baseUrl = this.getYandexAuthBaseUrl();
    const startUrl = new URL(`${baseUrl}/start`);
    startUrl.searchParams.set('origin', window.location.origin);

    this.setState({ isYandexLoading: true, authError: '', authNotice: '' });
    window.location.assign(startUrl.toString());
  };

  getYandexAuthBaseUrl = () => (
    process.env.REACT_APP_YANDEX_AUTH_URL || DEFAULT_YANDEX_AUTH_URL
  ).replace(/\/$/, '');

  handleYandexPasswordSubmit = async (event) => {
    event.preventDefault();
    const {
      yandexPasswordMode,
      yandexPasswordTicket,
      yandexEmail,
      yandexPassword,
      yandexPasswordConfirmation,
    } = this.state;

    if (yandexPasswordMode === 'setup' && yandexPassword !== yandexPasswordConfirmation) {
      this.setState({
        authError: 'Пароли не совпадают.',
        authNotice: '',
      });
      return;
    }

    this.setState({
      isYandexPasswordLoading: true,
      authError: '',
      authNotice: '',
    });

    try {
      if (yandexPasswordMode === 'setup') {
        const response = await fetch(`${this.getYandexAuthBaseUrl()}/complete-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticket: yandexPasswordTicket,
            password: yandexPassword,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok && result.error !== 'password_already_configured') {
          throw new Error(result.error || 'password_setup_failed');
        }
      }

      await signInWithEmailAndPassword(getAuth(), yandexEmail, yandexPassword);
      this.setState({
        yandexPasswordMode: '',
        yandexPasswordTicket: '',
        yandexPassword: '',
        yandexPasswordConfirmation: '',
      });
    } catch (error) {
      console.error('Yandex password authentication failed:', error);
      const isWrongPassword = [
        'auth/invalid-credential',
        'auth/wrong-password',
        'auth/invalid-login-credentials',
      ].includes(error.code);
      this.setState({
        authError: isWrongPassword
          ? 'Неверный пароль ZielStk. Попробуйте ещё раз или восстановите пароль.'
          : yandexPasswordMode === 'setup'
            ? 'Не удалось сохранить пароль. Повторите вход через Яндекс.'
            : 'Не удалось войти. Проверьте пароль и попробуйте ещё раз.',
      });
    } finally {
      this.setState({ isYandexPasswordLoading: false });
    }
  };

  handleYandexPasswordReset = async () => {
    const { yandexEmail } = this.state;
    try {
      await sendPasswordResetEmail(getAuth(), yandexEmail);
      this.setState({
        authError: '',
        authNotice: `Ссылка для восстановления отправлена на ${yandexEmail}.`,
      });
    } catch (error) {
      console.error('Yandex-linked password reset failed:', error);
      this.setState({
        authError: 'Не удалось отправить письмо для восстановления пароля.',
        authNotice: '',
      });
    }
  };

  cancelYandexPassword = () => {
    this.setState({
      isYandexLoading: false,
      yandexPasswordMode: '',
      yandexPasswordTicket: '',
      yandexEmail: '',
      yandexPassword: '',
      yandexPasswordConfirmation: '',
      isYandexPasswordLoading: false,
      authError: '',
      authNotice: '',
    });
  };

  handleEmailAuth = async (event) => {
    event.preventDefault();
    const { email, password, isRegistering } = this.state;

    this.setState({
      isEmailAuthLoading: true,
      authError: '',
      authNotice: '',
    });

    try {
      if (isRegistering) {
        await createUserWithEmailAndPassword(getAuth(), email, password);
      } else {
        await signInWithEmailAndPassword(getAuth(), email, password);
      }
    } catch (error) {
      console.error('Email authentication failed:', error);
      this.setState({
        authError: isRegistering
          ? 'Не удалось создать аккаунт. Проверьте email и пароль.'
          : 'Не удалось войти. Проверьте email и пароль.',
      });
    } finally {
      this.setState({ isEmailAuthLoading: false });
    }
  };

  handlePasswordReset = async () => {
    const { email } = this.state;
    if (!email) {
      this.setState({
        authError: 'Сначала укажите email для восстановления пароля.',
        authNotice: '',
      });
      return;
    }

    try {
      await sendPasswordResetEmail(getAuth(), email);
      this.setState({
        authError: '',
        authNotice: 'Ссылка для восстановления отправлена на указанный email.',
      });
    } catch (error) {
      console.error('Password reset failed:', error);
      this.setState({
        authError: 'Не удалось отправить письмо для восстановления пароля.',
        authNotice: '',
      });
    }
  };

  toggleEmailAuthMode = () => {
    this.setState((state) => ({
      isRegistering: !state.isRegistering,
      authError: '',
      authNotice: '',
    }));
  };

  Logout = () =>
    getAuth().signOut().then(() => {
      this.setState({ isSignedIn: false });
    });

  render() {
    const {
      isSignedIn,
      totalNumber,
      examNumber,
      themesNumber,
      isYandexLoading,
      yandexPasswordMode,
      yandexEmail,
      yandexPassword,
      yandexPasswordConfirmation,
      isYandexPasswordLoading,
      email,
      password,
      isRegistering,
      isEmailAuthLoading,
      authError,
      authNotice,
    } = this.state;

    if (!isSignedIn) {
      return (
        <ModernLanding
          totalNumber={totalNumber}
          examNumber={examNumber}
          themesNumber={themesNumber}
          isYandexLoading={isYandexLoading}
          yandexPasswordMode={yandexPasswordMode}
          yandexEmail={yandexEmail}
          yandexPassword={yandexPassword}
          yandexPasswordConfirmation={yandexPasswordConfirmation}
          isYandexPasswordLoading={isYandexPasswordLoading}
          email={email}
          password={password}
          isRegistering={isRegistering}
          isEmailAuthLoading={isEmailAuthLoading}
          authError={authError}
          authNotice={authNotice}
          onYandexSignIn={this.signInWithYandex}
          onYandexPasswordChange={(event) => this.setState({ yandexPassword: event.target.value })}
          onYandexPasswordConfirmationChange={(event) => this.setState({
            yandexPasswordConfirmation: event.target.value,
          })}
          onYandexPasswordSubmit={this.handleYandexPasswordSubmit}
          onYandexPasswordReset={this.handleYandexPasswordReset}
          onCancelYandexPassword={this.cancelYandexPassword}
          onEmailChange={(event) => this.setState({ email: event.target.value })}
          onPasswordChange={(event) => this.setState({ password: event.target.value })}
          onEmailAuth={this.handleEmailAuth}
          onPasswordReset={this.handlePasswordReset}
          onToggleEmailAuthMode={this.toggleEmailAuthMode}
        />
      );
    }

    const currentUser = getAuth().currentUser;

    return (
      <div id="main-div-block" className="app-shell">
        <div className="app-shell__glow app-shell__glow--blue" />
        <div className="app-shell__glow app-shell__glow--yellow" />
        <div className="app-main">
          <BrowserRouter>
            <div className="app-layout">
              <Menu
                imageUrl={currentUser.photoURL}
                displayName={currentUser.displayName}
                userEmail={currentUser.email}
                auth={this.Logout}
              />
              <main className="app-content">
                <Routes>
                  <Route path="/list" element={<PurchaseList user={currentUser} />} />
                  <Route path="/comp" element={<Completion />} />
                  <Route path="/shop" element={<ShopItem />} />
                  <Route path="/cart" element={<Kart />} />
                  <Route path="/support" element={<SupportForm />} />
                  <Route path="/" element={<Navigate to="/shop" replace />} />
                </Routes>
              </main>
            </div>
          </BrowserRouter>
        </div>
        <Footer />
      </div>
    );
  }
}
